import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  signal,
  ViewChildren,
  WritableSignal
} from '@angular/core';
import { LMBuilderEvent, LMBuilderModel } from "@components/builder/services/lm-builder-model.service";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { SohoAlertDirective } from "ids-enterprise-ng";
import { LMErrorUtilService } from "@app/services";
import { CdkDrag, CdkDragDrop, CdkDropList } from "@angular/cdk/drag-drop";

@Component({
  selector:    'lm-builder-filter',
  standalone: false,
  templateUrl: './lm-builder-filter.component.html',
  styleUrl: './lm-builder-filter.component.scss',
})
export class LMBuilderFilterComponent implements OnInit, OnDestroy {
  @Input() spec!: LMListPSpec;

  @ViewChildren(SohoAlertDirective) alerts: QueryList<SohoAlertDirective> | undefined;

  reportHasBaseFilter: WritableSignal<boolean> = signal(false);
  canRevertBaseFilter: WritableSignal<boolean> = signal(false);
  conditionTerms: WritableSignal<Array<LMBuilderConditionTerm>> = signal([]);
  conditionTermsIssue: WritableSignal<boolean> = signal(false);
  reportBaseFilter = '';
  filterCriteria = '';
  instanceSelectionValidator$:Subject<string> | undefined;

  protected readonly Soho = Soho;
  protected readonly LMBuilderModel = LMBuilderModel;
  private destroyer$ = new Subject<void>();


  constructor(
      private lmBuilderModel: LMBuilderModel,
      private lmErrorUtilService: LMErrorUtilService,
  ) {
    //no-op
  }

  ngOnInit(): void {
    this.lmBuilderModel.builderEvent$.pipe(
        takeUntil(this.destroyer$))
        .subscribe(event => {
          this.handleModelEvent(event);
        });
    this.initialize();
  }

  ngOnDestroy() {
    this.destroy();
  }

  private initialize() {
    this.reportHasBaseFilter.set(this.lmBuilderModel.reportHasBaseFilter());
    this.reportBaseFilter = this.lmBuilderModel.reportBaseFilter();
    this.initializeFilter();

    if (!this.instanceSelectionValidator$) {
      this.instanceSelectionValidator$ = new Subject<string>();
      this.instanceSelectionValidator$.pipe(
          takeUntil(this.destroyer$),
          debounceTime(1250),
          distinctUntilChanged())
          .subscribe(value => {
            this.validateFilter();
          });
    }
  }

  private initializeFilter() {
    this.filterCriteria = this.lmBuilderModel.getFilterCriteria();
    const terms = this.lmBuilderModel.getConditionTerms();

    if (terms) {
      this.conditionTerms.set(terms);
    } else {
      this.conditionTerms.set([]);
      this.conditionTermsIssue.set(true);
    }
  }

  private destroy() {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  private handleModelEvent(event:LMBuilderEvent) {
    if (event === LMBuilderEvent.FILTER_CHANGED ) {
      this.initializeFilter();
    }
  }

  canDrop = (drag: CdkDrag, drop: CdkDropList): boolean => {
    if (drag.dropContainer.data.includes('lm-builder-field')) {
      return true;
    }

    return false;
  };

  drop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer.data.includes('lm-builder-field')) {
      if (this.lmBuilderModel.allowColumnField(event.item.data)) {
        const fieldId = this.lmBuilderModel.buildFieldId(event.item.data);
        this.onAddCustomFilterClicked(fieldId);
      }
    }
  }

  onAddCustomFilterClicked(fieldId?: string) {
    this.lmBuilderModel.addCustomFilter(fieldId);
  }

  removeReportBaseFilter() {
    this.canRevertBaseFilter.set(true);
    this.lmBuilderModel.removeReportBaseFilter();
  }

  revertReportBasFilter() {
    this.canRevertBaseFilter.set(false);
    this.lmBuilderModel.revertReportBaseFilter();
  }

  private validateFilter() {
    this.lmBuilderModel.validateFilterCriteria(this.filterCriteria).pipe(
        takeUntil(this.destroyer$),
        debounceTime(1250),
        distinctUntilChanged()).subscribe({
      next: (v)       => { this.handleValidationResponse(v); },
      error: (e)                      => { this.handleError(e); },
      complete: ()                          => { }
    });
  }

  private handleValidationResponse(response: LMLplValidationResponse) {
    this.updateValidationAlert(response);
  }

  private handleError(error: any) {
    this.lmErrorUtilService.handleUnexpectedError(error);
  }

  private updateValidationAlert(lplValidationResponse?: LMLplValidationResponse) {
    const alert = this.alerts?.find((alert: SohoAlertDirective) => (alert.elementRef.nativeElement as Element).getAttribute('id') === 'lm-builder-list-filter');
    this.lmBuilderModel.updateFilterCriteria(this.filterCriteria);
    if (alert) {
      if (!lplValidationResponse || lplValidationResponse.lplStatus === 'LPL_VALID') {
        alert.removeAllMessages();

        if (lplValidationResponse?.conditionTerms) {
          this.lmBuilderModel.updateConditionTerms(lplValidationResponse.conditionTerms);
          this.conditionTerms.set(lplValidationResponse.conditionTerms);
          this.conditionTermsIssue.set(false);
        } else {
          this.lmBuilderModel.updateConditionTerms(undefined);
          this.conditionTerms.set([]);
          this.conditionTermsIssue.set(true);
        }
      } else {
        this.lmBuilderModel.updateConditionTerms(undefined);
        this.conditionTermsIssue.set(true);
        const validationError = lplValidationResponse.validationError;
        if (validationError) {
          let alertMessage = validationError.errorDescription;
          if (validationError.errorText) {
            alertMessage += ' ' + validationError.errorText;
          }
          const alertType = validationError && validationError.isWarning ? 'info' : 'error';
          alert.addInlineMessage(alertMessage, alertType, true, false, undefined, 'lm-builder-list-filter');
        } else {
          alert.addInlineMessage(lplValidationResponse.message ? lplValidationResponse.message : 'Unexpected Error', 'error', true, false, undefined, 'lm-builder-list-filter');
        }
      }
    }
  }
}
